import React, { useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Text,
  useAuthenticator,
  View,
} from '@aws-amplify/ui-react';
import { Image } from '../elements/image';
import logo from '../assets/images/logo.png';
import { Box, Typography } from '@mui/material';
import './amplify.css';
import { CircularProgress } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_PASSWORD_POLICY } from '../graphql/query';
import {
  PASSWORD_POLICY_MESSAGES,
  PASSWORD_POLICY_PREFIX,
} from '../constants/constants';
interface SignInHeaderProps {
  title?: string;
}
interface PasswordPolicy {
  minimumLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumbers: boolean;
  requireSymbols: boolean;
  temporaryPasswordValidityDays?: number;
}

export const SignInHeader = ({ title = 'Login' }: SignInHeaderProps) => {
  return (
    <View>
      <Box p={2}>
        <Heading level={3}>{title}</Heading>
      </Box>
    </View>
  );
};

export const SignInFooter = () => {
  const { toResetPassword } = useAuthenticator();
  return (
    <View textAlign="center">
      <Box p={2}>
        <Button
          fontWeight="normal"
          onClick={toResetPassword}
          size="small"
          variation="link"
        >
          Reset Password
        </Button>
      </Box>
    </View>
  );
};

export const Header = () => (
  <Box width={200} margin="auto" mb={2}>
    <Image src={logo} />
  </Box>
);

export const Footer = () => {
  return (
    <Box textAlign="center" mt={2}>
      <Text>&copy; All Rights Reserved: Liively</Text>
    </Box>
  );
};

export const PasswordPolicyMessage = () => {
  const { data, loading } = useQuery(GET_PASSWORD_POLICY);
  const [message, setMessage] = useState('');

  const formatPasswordPolicyMessage = (
    policy: PasswordPolicy | null,
  ): string => {
    if (!policy) return '';

    const ruleDefinitions: Record<
      keyof PasswordPolicy,
      (value: number | boolean | undefined) => string | null
    > = {
      minimumLength: (value) =>
        typeof value === 'number'
          ? PASSWORD_POLICY_MESSAGES.MIN_LENGTH(value)
          : null,

      requireUppercase: (value) =>
        value ? PASSWORD_POLICY_MESSAGES.REQUIRE_UPPERCASE : null,

      requireLowercase: (value) =>
        value ? PASSWORD_POLICY_MESSAGES.REQUIRE_LOWERCASE : null,

      requireNumbers: (value) =>
        value ? PASSWORD_POLICY_MESSAGES.REQUIRE_NUMBERS : null,

      requireSymbols: (value) =>
        value ? PASSWORD_POLICY_MESSAGES.REQUIRE_SYMBOLS : null,

      temporaryPasswordValidityDays: () => null,
    };

    const activeRules = Object.entries(ruleDefinitions)
      .map(([key, formatter]) => formatter(policy[key as keyof PasswordPolicy]))
      .filter(Boolean) as string[];

    if (activeRules.length === 0) return '';

    const formatList = (rules: string[]) =>
      rules.length === 1
        ? rules[0]
        : `${rules.slice(0, -1).join(', ')} and ${rules[rules.length - 1]}`;

    return `${PASSWORD_POLICY_PREFIX} ${formatList(activeRules)}.`;
  };

  useEffect(() => {
    if (data?.getPasswordPolicy) {
      setMessage(formatPasswordPolicyMessage(data.getPasswordPolicy));
    }
  }, [data]);

  if (loading) {
    return <CircularProgress size={16} />;
  }

  return (
    <Typography
      color="red"
      variant="body2"
      sx={{
        textAlign: 'center',
        display: 'block',
      }}
    >
      {message}
    </Typography>
  );
};
